import { useCallback, useReducer, useRef } from "react";
import type { PlayProps } from "../types";
/** Local, unsent drafts (clue text, bid, chosen target…) keyed by seat, match and
 * phase. A draft survives remounts, a pause (the server empties legalActions) and
 * opening tools within the same phase, and starts fresh in the next phase. Drafts
 * live only in this tab's memory: never sent, persisted or shown publicly. */
const store = new Map<string, unknown>();
const LIMIT = 200;
export function draftScope(state: PlayProps["state"]): string {
  return JSON.stringify([
    state.roomInstanceId ?? "",
    state.playerId ?? null,
    state.matchId ?? null,
    state.phaseId ?? state.game.public.phaseKey ?? null,
  ]);
}
export function readDraft<T>(scope: string, field: string, initial: T): T {
  const key = scope + "\u0000" + field;
  return store.has(key) ? (store.get(key) as T) : initial;
}
export function writeDraft<T>(scope: string, field: string, value: T) {
  const key = scope + "\u0000" + field;
  store.delete(key);
  store.set(key, value);
  while (store.size > LIMIT) store.delete(store.keys().next().value as string);
}
export function clearDrafts() {
  store.clear();
}
export function useDraft<T>(
  scope: string,
  field: string,
  initial: T,
): [T, (value: T | ((previous: T) => T)) => void] {
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const latest = useRef(initial);
  latest.current = initial;
  const value = readDraft(scope, field, initial);
  const set = useCallback(
    (next: T | ((previous: T) => T)) => {
      const previous = readDraft(scope, field, latest.current);
      writeDraft(
        scope,
        field,
        typeof next === "function"
          ? (next as (previous: T) => T)(previous)
          : next,
      );
      rerender();
    },
    [scope, field],
  );
  return [value, set];
}
